import React, { useEffect, useRef, useState }    from 'react';
import _                                         from 'underscore';
import { Divider, Menu, Segment, SemanticICONS } from 'semantic-ui-react';
import { useQuery }                              from '@apollo/react-hooks';

import { CorvalProject, Size, Uuid }                                                                     from '@src/types';
import { useCurrentUser }                                                                                from '@src/hooks';
import { queriesHaveData, queriesLoading }                                                               from '@src/store/_apolloUtil';
import { ProjectQueries }                                                                                from '@src/queries/ProjectQueries';
import { ENTITY_ICONS }                                                                                  from '@src/constants';
import { StyledContainer }                                                                               from '@src/views/containers';
import { UseIf }                                                                                         from '@src/views/components/display/EqualValues';
import { LoadingIndicator }                                                                              from '@src/views/components/util/loading/LoadingIndicator';
import { MenuItemWithIconAndIndicator }                                                                  from '@src/views/components/menus/MenuItemWithIconAndIndicator';
import { MenuizedFocusContainerLayout }                                                                  from "@src/views/containers/MenuizedFocusContainerLayout";
import { BudgetCostTypesByProject }                                                                      from '../../chart-reports/multi-project/BudgetCostTypesByProject';
import { ActivityCostByBusinessFunctionYearlyByProject }                                                 from '../../chart-reports/multi-project/ActivityCostByBusinessFunctionYearlyByProject';
import { ActivityCountByBusinessFunctionYearlyByProject }                                                from '../../chart-reports/multi-project/ActivityCountByBusinessFunctionYearlyByProject';
import { HiringCostByBusinessFunctionYearlyByProject }                                                   from '../../chart-reports/multi-project/HiringCostByBusinessFunctionYearlyByProject';
import { HiringCountByBusinessFunctionYearlyByProject }                                                  from '../../chart-reports/multi-project/HiringCountByBusinessFunctionYearlyByProject';
import { compareProjectCostTypesToolDefaultOptions, CompareProjectCostTypesToolOptions }                 from './CompareProjectCostTypesToolOptions';
import { compareProjectBusinessFunctionsToolDefaultOptions, CompareProjectBusinessFunctionsToolOptions } from './CompareProjectBusinessFunctionsToolOptions';
import { compareProjectToggleToolDefaultOptions, CompareProjectToggleToolOptions }                       from './CompareProjectToggleToolOptions';


export enum CompareToolType {
    BUDGET_BY_COST_TYPE                        = 'BUDGET_BY_COST_TYPE',
    ACTIVITY_COST_BY_BUSINESS_FUNCTION_YEARLY  = 'ACTIVITY_COST_BY_BUSINESS_FUNCTION_YEARLY',
    ACTIVITY_COUNT_BY_BUSINESS_FUNCTION_YEARLY = 'ACTIVITY_COUNT_BY_BUSINESS_FUNCTION_YEARLY',
    HIRING_COST_BY_BUSINESS_FUNCTION_YEARLY    = 'HIRING_COST_BY_BUSINESS_FUNCTION_YEARLY',
    HIRING_COUNT_BY_BUSINESS_FUNCTION_YEARLY   = 'HIRING_COUNT_BY_BUSINESS_FUNCTION_YEARLY',
}

export type CompareToolTypeConfig = {
    menuLabel: string,
    menuIcon: SemanticICONS,
    onMenuClick: () => void,
    hasOptions: boolean,
    minOptionWidth: string,
    maxOptionWidth: string,
}

export interface CompareProjectToolProps<T = never> {
    projectIds?: Uuid[],
    primaryProjectId?: Uuid | null
    comparisonType?: CompareToolType,
}

type CompareToolContextType = {
    toolOptionsData: any,
    selectedProjectIds: Uuid[],
    comparisonType: CompareToolType,
}

export const CompareToolContext = React.createContext<CompareToolContextType>( {
    toolOptionsData:    null,
    selectedProjectIds: [],
    comparisonType:     CompareToolType.BUDGET_BY_COST_TYPE,
} );

const CompareProjectToolDefaultProps = {
    comparisonType: CompareToolType.BUDGET_BY_COST_TYPE,
} satisfies Partial<CompareProjectToolProps>;

function CompareProjectTool<T>( p: CompareProjectToolProps<T> ){

    // defaults / user props
    const props = { ...CompareProjectToolDefaultProps, ...p };

    const { projectIds, primaryProjectId, comparisonType } = props;
    const { currentUser } = useCurrentUser();
    const projectDictionaryRef = useRef( null );
    const [ compareToolContextValue, setCompareToolContextValue ] = useState<CompareToolContextType>( {
        toolOptionsData:    null,
        selectedProjectIds: projectIds,
        comparisonType:     comparisonType,
    } );

    const primaryProject = _.findWhere( currentUser.projects, { projectId: primaryProjectId } );
    const primaryId = !_.isEmpty( primaryProject ) ? primaryProject.projectId : null;

    const dictionaryQuery = useQuery<{ projects: CorvalProject[] }>(
        ProjectQueries.queries.GET_PROJECT( 'id termDictionary' ), {
            skip:      !primaryId,
            variables: {
                id: primaryId
            }
        } );

    useEffect( () => {

        if( !queriesLoading( dictionaryQuery ) && queriesHaveData( dictionaryQuery ) ){

            projectDictionaryRef.current = dictionaryQuery.data.projects[ 0 ].termDictionary;
        }

    }, [ dictionaryQuery ] );

    const handleComparisonViewChanged = ( comparisonType: any ) => {

        setCompareToolContextValue( prevState => ( { ...prevState, comparisonType, toolOptionsData: {} } ) );
    };

    const handleToolOptionsChanged = ( data: any ) => {

        setCompareToolContextValue( prevState => ( { ...prevState, toolOptionsData: data } ) );
    };

    const handleToolOptionPropChanged = ( dataObject: object ) => {

        const toolOptionsData = ( _.isObject( compareToolContextValue.toolOptionsData ) )
                                ? { ...compareToolContextValue.toolOptionsData, ...dataObject }
                                : dataObject;

        handleToolOptionsChanged( toolOptionsData );
    };

    const comparisonTypeConfigs: Record<CompareToolType, CompareToolTypeConfig> = {
        [ CompareToolType.BUDGET_BY_COST_TYPE ]:                        {
            menuLabel:      'Budgets by Cost Type',
            menuIcon:       ENTITY_ICONS.BUDGET.REPORTING,
            onMenuClick:    () => {
                handleComparisonViewChanged( CompareToolType.BUDGET_BY_COST_TYPE );
                handleToolOptionsChanged( compareProjectCostTypesToolDefaultOptions );
            },
            hasOptions:     true,
            minOptionWidth: '150px',
            maxOptionWidth: 'auto'
        },
        [ CompareToolType.ACTIVITY_COST_BY_BUSINESS_FUNCTION_YEARLY ]:  {
            menuLabel:      'Activity Costs Yearly',
            menuIcon:       ENTITY_ICONS.ACTIVITIES.ENTITY,
            onMenuClick:    () => {
                handleComparisonViewChanged( CompareToolType.ACTIVITY_COST_BY_BUSINESS_FUNCTION_YEARLY );
                handleToolOptionsChanged( {
                    compileBusinessFunctions:  compareToolContextValue.toolOptionsData?.compileBusinessFunctions ?? compareProjectToggleToolDefaultOptions,
                    includedBusinessFunctions: compareToolContextValue.toolOptionsData?.includedBusinessFunctions ?? compareProjectBusinessFunctionsToolDefaultOptions
                } );
            },
            hasOptions:     true,
            minOptionWidth: '0px',
            maxOptionWidth: 'auto'
        },
        [ CompareToolType.ACTIVITY_COUNT_BY_BUSINESS_FUNCTION_YEARLY ]: {
            menuLabel:      'Activity Count Yearly',
            menuIcon:       ENTITY_ICONS.ACTIVITIES.ENTITY,
            onMenuClick:    () => {
                handleComparisonViewChanged( CompareToolType.ACTIVITY_COUNT_BY_BUSINESS_FUNCTION_YEARLY );
                handleToolOptionsChanged( {
                    compileBusinessFunctions:  compareToolContextValue.toolOptionsData?.compileBusinessFunctions ?? compareProjectToggleToolDefaultOptions,
                    includedBusinessFunctions: compareToolContextValue.toolOptionsData?.includedBusinessFunctions ?? compareProjectBusinessFunctionsToolDefaultOptions
                } );
            },
            hasOptions:     true,
            minOptionWidth: '0px',
            maxOptionWidth: 'auto'
        },
        [ CompareToolType.HIRING_COST_BY_BUSINESS_FUNCTION_YEARLY ]:    {
            menuLabel:      'Hiring Costs Yearly',
            menuIcon:       ENTITY_ICONS.RESOURCING.HIRES.ENTITY,
            onMenuClick:    () => {
                handleComparisonViewChanged( CompareToolType.HIRING_COST_BY_BUSINESS_FUNCTION_YEARLY );
                handleToolOptionsChanged( {
                    compileBusinessFunctions:  compareToolContextValue.toolOptionsData?.compileBusinessFunctions ?? compareProjectToggleToolDefaultOptions,
                    includedBusinessFunctions: compareToolContextValue.toolOptionsData?.includedBusinessFunctions ?? compareProjectBusinessFunctionsToolDefaultOptions
                } );
            },
            hasOptions:     true,
            minOptionWidth: '0px',
            maxOptionWidth: 'auto'
        },
        [ CompareToolType.HIRING_COUNT_BY_BUSINESS_FUNCTION_YEARLY ]:   {
            menuLabel:      'Hiring Count Yearly',
            menuIcon:       ENTITY_ICONS.RESOURCING.HIRES.ENTITY,
            onMenuClick:    () => {
                handleComparisonViewChanged( CompareToolType.HIRING_COUNT_BY_BUSINESS_FUNCTION_YEARLY );
                handleToolOptionsChanged( {
                    compileBusinessFunctions:  compareToolContextValue.toolOptionsData?.compileBusinessFunctions ?? compareProjectToggleToolDefaultOptions,
                    includedBusinessFunctions: compareToolContextValue.toolOptionsData?.includedBusinessFunctions ?? compareProjectBusinessFunctionsToolDefaultOptions
                } );
            },
            hasOptions:     true,
            minOptionWidth: '0px',
            maxOptionWidth: 'auto'
        }
    };

    // the layout is the current comparison type's config
    const currentLayout = {
        ...comparisonTypeConfigs[ compareToolContextValue.comparisonType ],
    };

    return (

        <>
            <MenuizedFocusContainerLayout
                minContainerHeight={ '500px' }
                menuContent={ (
                    <>
                        {/* View Toggle Menu */ }
                        <Menu fluid vertical size={ 'small' }>

                            { _.pairs( comparisonTypeConfigs ).map( ( [ type, config ] ) => (

                                <MenuItemWithIconAndIndicator
                                    menuIcon={ config.menuIcon }
                                    onClick={ config.onMenuClick }
                                    withIndicator={ false }
                                    isActive={ compareToolContextValue.comparisonType === type }>{ config.menuLabel }</MenuItemWithIconAndIndicator>

                            ) ) }

                        </Menu>
                    </>
                ) }
                viewContent={ (
                    <CompareToolContext.Provider value={ compareToolContextValue }>

                        { ( !primaryId || projectDictionaryRef !== null )
                          ? (
                              <Segment raised>

                                  <StyledContainer padding={ Size.MD }>

                                      <UseIf x={ compareToolContextValue.comparisonType } eq={ CompareToolType.BUDGET_BY_COST_TYPE }>

                                          <BudgetCostTypesByProject
                                              showTitle={ false }
                                              enableTableToggle={ true }
                                              enableImageDownload={ true }
                                              enableCopyDataText={ true }
                                              enableCSVDownload={ true }
                                              primaryProjectId={ primaryProjectId }
                                              projectIds={ compareToolContextValue.selectedProjectIds }
                                              costTypes={ compareToolContextValue.toolOptionsData as [] }
                                              termDictionary={ projectDictionaryRef.current }
                                          />

                                      </UseIf>

                                      <UseIf x={ compareToolContextValue.comparisonType } eq={ CompareToolType.ACTIVITY_COST_BY_BUSINESS_FUNCTION_YEARLY }>

                                          <ActivityCostByBusinessFunctionYearlyByProject
                                              showTitle={ false }
                                              renderAsChart={ true }
                                              enableTableToggle={ true }
                                              enableImageDownload={ true }
                                              enableCopyDataText={ true }
                                              enableCSVDownload={ true }
                                              primaryProjectId={ primaryProjectId }
                                              projectIds={ compareToolContextValue.selectedProjectIds }
                                              termDictionary={ projectDictionaryRef.current }
                                              compileBusinessFunctions={ compareToolContextValue.toolOptionsData?.compileBusinessFunctions ?? true }
                                              includedBusinessFunctions={ compareToolContextValue.toolOptionsData?.includedBusinessFunctions ?? null }
                                          />

                                      </UseIf>

                                      <UseIf x={ compareToolContextValue.comparisonType } eq={ CompareToolType.ACTIVITY_COUNT_BY_BUSINESS_FUNCTION_YEARLY }>

                                          <ActivityCountByBusinessFunctionYearlyByProject
                                              showTitle={ false }
                                              renderAsChart={ true }
                                              enableTableToggle={ true }
                                              enableImageDownload={ true }
                                              enableCopyDataText={ true }
                                              enableCSVDownload={ true }
                                              primaryProjectId={ primaryProjectId }
                                              projectIds={ compareToolContextValue.selectedProjectIds }
                                              termDictionary={ projectDictionaryRef.current }
                                              compileBusinessFunctions={ compareToolContextValue.toolOptionsData?.compileBusinessFunctions ?? true }
                                              includedBusinessFunctions={ compareToolContextValue.toolOptionsData?.includedBusinessFunctions ?? null }
                                          />

                                      </UseIf>

                                      <UseIf x={ compareToolContextValue.comparisonType } eq={ CompareToolType.HIRING_COST_BY_BUSINESS_FUNCTION_YEARLY }>

                                          <HiringCostByBusinessFunctionYearlyByProject
                                              showTitle={ false }
                                              renderAsChart={ true }
                                              enableTableToggle={ true }
                                              enableImageDownload={ true }
                                              enableCopyDataText={ true }
                                              enableCSVDownload={ true }
                                              primaryProjectId={ primaryProjectId }
                                              projectIds={ compareToolContextValue.selectedProjectIds }
                                              termDictionary={ projectDictionaryRef.current }
                                              compileBusinessFunctions={ compareToolContextValue.toolOptionsData?.compileBusinessFunctions ?? true }
                                              includedBusinessFunctions={ compareToolContextValue.toolOptionsData?.includedBusinessFunctions ?? null }
                                          />

                                      </UseIf>

                                      <UseIf x={ compareToolContextValue.comparisonType } eq={ CompareToolType.HIRING_COUNT_BY_BUSINESS_FUNCTION_YEARLY }>

                                          <HiringCountByBusinessFunctionYearlyByProject
                                              showTitle={ false }
                                              renderAsChart={ true }
                                              enableTableToggle={ true }
                                              enableImageDownload={ true }
                                              enableCopyDataText={ true }
                                              enableCSVDownload={ true }
                                              primaryProjectId={ primaryProjectId }
                                              projectIds={ compareToolContextValue.selectedProjectIds }
                                              termDictionary={ projectDictionaryRef.current }
                                              compileBusinessFunctions={ compareToolContextValue.toolOptionsData?.compileBusinessFunctions ?? true }
                                              includedBusinessFunctions={ compareToolContextValue.toolOptionsData?.includedBusinessFunctions ?? null }
                                          />

                                      </UseIf>

                                  </StyledContainer>

                              </Segment>

                          ) : (

                              <StyledContainer fill={ 'area' }>

                                  <LoadingIndicator isActive={ queriesLoading( dictionaryQuery ) }/>

                              </StyledContainer>

                          ) }

                    </CompareToolContext.Provider>

                ) }
                optionsContent={ !currentLayout.hasOptions ? null : (
                    <>
                        <UseIf x={ compareToolContextValue.comparisonType } eq={ CompareToolType.BUDGET_BY_COST_TYPE }>

                            <CompareProjectCostTypesToolOptions
                                onChange={ handleToolOptionsChanged }
                                initialValue={ compareToolContextValue.toolOptionsData as [] ?? undefined }
                            />

                        </UseIf>

                        <UseIf x={ compareToolContextValue.comparisonType } eq={ [
                            CompareToolType.ACTIVITY_COST_BY_BUSINESS_FUNCTION_YEARLY,
                            CompareToolType.ACTIVITY_COUNT_BY_BUSINESS_FUNCTION_YEARLY,
                            CompareToolType.HIRING_COST_BY_BUSINESS_FUNCTION_YEARLY,
                            CompareToolType.HIRING_COUNT_BY_BUSINESS_FUNCTION_YEARLY,
                        ] }>

                            <UseIf x={ props.projectIds.length } eq={ 1 }>

                                <>
                                    <CompareProjectToggleToolOptions
                                        header={ 'Display' }
                                        label={ 'Compile Business Funtions' }
                                        initialValue={ compareToolContextValue.toolOptionsData?.compileBusinessFunctions ?? undefined }
                                        onChange={ value => handleToolOptionPropChanged( { compileBusinessFunctions: value } ) }/>

                                    <Divider/>
                                </>

                            </UseIf>

                            <CompareProjectBusinessFunctionsToolOptions
                                key={ compareToolContextValue.comparisonType }
                                initialValue={ compareToolContextValue.toolOptionsData?.includedBusinessFunctions ?? undefined }
                                onChange={ value => handleToolOptionPropChanged( { includedBusinessFunctions: value } ) }/>

                        </UseIf>
                    </>
                ) }
            />
        </>

    );
}

export {
    CompareProjectTool
};
